/**
 * Jelly nails — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_nail_looks`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const jellyNailsPack: ToilePack = {
    slug: 'jelly-nails',
    category: 'beauty',
    name: 'Jelly nails',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'single', label: 'Red jelly', subject: 'short round nails in a translucent red jelly gel with the nail tip showing through like candy'},
        {key: 'rainbow', label: 'Rainbow jelly', subject: 'almond nails each in a different see-through jelly colour, orange, yellow, green, blue and purple'},
        {key: 'glitter', label: 'Glitter jelly', subject: 'oval nails in clear pink jelly gel with fine silver glitter suspended inside'},
    ],
    styles: [
        {key: 'window', label: 'Window light', style: 'held up toward a bright window with daylight from behind passing through the gel, a bright backlit balance so each nail glows like coloured glass', palette: 'Pale sky white behind, the jelly colours lit from within', framing: 'The hand raised near-frontal, fingers a little apart, the window a soft white blur'},
        {key: 'candy', label: 'Candy', style: 'resting among a few clear boiled sweets on a pastel mint surface in soft daylight from camera-left, a bright cheerful balance', palette: 'Pastel mint and candy colours echoing the gel', framing: 'Seen from above, the hand across the centre with the sweets near the edges'},
        {key: 'pool', label: 'Poolside', style: 'resting on the edge of a pool in bright summer sun from above, rippling water glints on the skin, a vivid high balance', palette: 'Turquoise water and warm skin, the jelly colours saturated', framing: 'A near-frontal view at a low angle, the hand on pale tiles and the water filling the background'},
    ],
    directions: ['jelly french', 'pastel jelly', 'toe nails'],
    directionHint: 'try: jelly french',
    seo: {
        title: 'Jelly Nails: Red Jelly, Rainbow, Glitter Jelly',
        metaDescription: 'Nine jelly nail designs: a see-through red gel, a rainbow of jelly shades and clear pink with silver glitter, in three settings.',
        h1: 'Jelly nails: red jelly, rainbow jelly and glitter jelly',
        tagline: 'Nine jelly sets: three see-through designs, each held to a window, set on candy and by a pool.',
        intro: 'Jelly nails for people who want a see-through colored gel that looks like candy or glass. The three designs are short round nails in translucent red jelly, almond nails each in a different see-through shade from orange to purple, and oval nails in clear pink gel with fine silver glitter inside. Each is shown held to a window, set among clear candies and by a pool. Type a different jelly color and the same translucent gel carries it.',
        useCases: [
            'a single jelly color for summer',
            'a rainbow set for a party',
            'glitter jelly for a special evening',
            'a pastel jelly for spring',
        ],
        faq: [
            {q: 'What does jelly mean here?', a: 'A translucent colored gel, so the nail tip shows through as if it were candy or glass.'},
            {q: 'Why are some settings in light?', a: 'Light passing through or around the nail shows how the clear color reads as glass rather than paint.'},
            {q: 'Can I ask for a different jelly color?', a: 'Yes. A color you type is a request, not one of the nine pictures shown.'},
        ],
    },
}
